import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Download, TrendingUp, TrendingDown, Users, Target, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClassAnalytics() {
  const { project_id } = useParams();
  const navigate = useNavigate();
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [project_id]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marking/projects/${project_id}/class-analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error(error.response?.data?.detail || 'Failed to load analytics');
      navigate('/assessment');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API}/marking/projects/${project_id}/export/csv`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${analytics.project_name}_results.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API}/marking/projects/${project_id}/export/excel`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${analytics.project_name}_results.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading analytics...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={() => navigate(`/assessment/project/${project_id}`)}
                className="text-purple-600 hover:text-purple-700 font-medium mb-4 inline-flex items-center gap-2"
              >
                ← Back to Project
              </button>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Class Analytics</h1>
              <p className="text-base text-slate-600">{analytics.project_name}</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={downloadCSV}
                disabled={downloading}
                className="bg-blue-600 text-white hover:bg-blue-700 px-5 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2 disabled:bg-slate-300"
              >
                <FileText className="w-5 h-5" />
                Export CSV
              </button>
              <button
                onClick={downloadExcel}
                disabled={downloading}
                className="bg-green-600 text-white hover:bg-green-700 px-5 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2 disabled:bg-slate-300"
              >
                <FileSpreadsheet className="w-5 h-5" />
                Export Excel
              </button>
            </div>
          </div>

          {/* Class Summary Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-600" />
                <span className="text-3xl font-bold text-slate-900">{analytics.total_students}</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Total Students</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <span className="text-3xl font-bold text-slate-900">{analytics.class_average.toFixed(1)}%</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Class Average</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-8 h-8 text-purple-600" />
                <span className="text-3xl font-bold text-slate-900">{analytics.highest_score.toFixed(1)}%</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Highest Score</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <TrendingDown className="w-8 h-8 text-amber-600" />
                <span className="text-3xl font-bold text-slate-900">{analytics.lowest_score.toFixed(1)}%</span>
              </div>
              <p className="text-sm font-semibold text-slate-600">Lowest Score</p>
            </div>
          </div>

          {/* Grade Distribution */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Grade Distribution</h2>
            
            <div className="space-y-4">
              {Object.entries(analytics.grade_distribution).map(([grade, count]) => {
                const percentage = (count / analytics.total_students) * 100;
                const colors = {
                  'A (90-100%)': 'bg-green-500',
                  'B (80-89%)': 'bg-blue-500',
                  'C (70-79%)': 'bg-yellow-500',
                  'D (60-69%)': 'bg-orange-500',
                  'F (Below 60%)': 'bg-red-500'
                };
                
                return (
                  <div key={grade}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-base font-semibold text-slate-900">{grade}</span>
                      <span className="text-base font-bold text-slate-900">{count} student{count !== 1 ? 's' : ''} ({percentage.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full ${colors[grade]}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Question-wise Analysis */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Question-wise Performance</h2>
            
            <div className="space-y-6">
              {analytics.question_analysis.map((question) => (
                <div key={question.question_number} className="border border-slate-200 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        Question {question.question_number}
                      </h3>
                      <p className="text-sm text-slate-600 mb-2">{question.question_text}</p>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="text-slate-600">
                          <strong>Max Marks:</strong> {question.max_marks}
                        </span>
                        <span className="text-slate-600">
                          <strong>Attempted by:</strong> {question.students_attempted} students
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-lg ${
                        question.success_rate >= 75
                          ? 'bg-green-100 text-green-700'
                          : question.success_rate >= 50
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {question.success_rate >= 75 ? '✓' : question.success_rate >= 50 ? '◐' : '✗'}
                        {' '}{question.success_rate.toFixed(0)}%
                      </div>
                      <p className="text-sm text-slate-600 mt-2">Success Rate</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Average Marks</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {question.average_marks.toFixed(2)} / {question.max_marks}
                      </p>
                    </div>
                    
                    {question.common_errors.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">Common Errors</p>
                        <ul className="space-y-1">
                          {question.common_errors.map((error, idx) => (
                            <li key={idx} className="text-sm text-slate-600">• {error}...</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Performance Bar */}
                  <div className="mt-4">
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${
                          question.success_rate >= 75
                            ? 'bg-green-500'
                            : question.success_rate >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${question.success_rate}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <p>Analytics generated on {new Date(analytics.generated_at).toLocaleDateString()}</p>
            <p className="mt-1">Learn with Zeenat • AI-Assisted Assessment</p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ClassAnalytics;
